// Vuex
import { mapActions, mapGetters, mapState } from 'vuex'
// Utils
import { checkPermissions, featureAccess } from '@/utils/accesses'
import { isOwnOrganizationScope } from '@/utils/organization-scope'

/**
 * The navigation menu, generated from the router.
 *
 * A route that carries `meta.menu` (see src/router/index.js) becomes a menu
 * item here — section, order, icons and the declarative access rules all come
 * from that one place, so the sidebar and the mission-control grid pick up a
 * new page automatically.
 */

// Section order and headers for the `meta.menu.section` values.
const MENU_SECTIONS = [
  { key: 'dashboard', storageKey: 'sidebarSectionDashboard' },
  { key: 'investigations', title: 'INVESTIGATIONS', storageKey: 'sidebarSectionInvestigations' },
  { key: 'kyt', title: 'KNOW YOUR TRANSACTION', storageKey: 'sidebarSectionKyt' },
  { key: 'kyb', title: 'KNOW YOUR BUSINESS', storageKey: 'sidebarSectionKyb' },
  { key: 'settings', title: 'SETTINGS', storageKey: 'sidebarSectionSettings' },
  { key: 'admin', title: 'ADMIN SETTINGS', storageKey: 'sidebarSectionAdmin' },
]

// Menu-only presentation fields copied from `meta.menu` onto the link as-is.
const PASSTHROUGH_FIELDS = ['iconTone', 'activeIconTone', 'iconHover', 'notifyKey']

export default {
  computed: {
    ...mapGetters('user', ['permissions']),
    ...mapState('alerts', ['unreadNotificationCount']),

    // Every route annotated with meta.menu, flattened and ordered.
    routeMenuLinks() {
      const links = []

      const walk = routes => (routes || []).forEach(route => {
        if (route.meta && route.meta.menu) links.push(this.buildMenuLink(route))
        walk(route.children)
      })

      walk(this.$router.options.routes)

      return links.sort((a, b) => (a.order || 0) - (b.order || 0))
    },

    menuSections() {
      return MENU_SECTIONS.map(section => ({
        key: section.key,
        title: section.title,
        storageKey: section.storageKey,
        links: this.routeMenuLinks.filter(link => link.section === section.key),
      }))
    },

    footerLinks() {
      const actions = [
        {
          key: 'support',
          order: 20,
          title: 'Contact Support',
          action: this.contactSupport,
          icon: 'Contact-support',
          tooltip: 'Contact Support',
          isVisible: true,
        },
        {
          key: 'logout',
          order: 40,
          title: 'Logout',
          action: this.logoutUser,
          icon: 'Logout',
          tooltip: 'Logout',
          isVisible: true,
        },
      ]

      return this.routeMenuLinks
        .filter(link => link.section === 'footer')
        .concat(actions)
        .sort((a, b) => (a.order || 0) - (b.order || 0))
    },

    visibleMenuSections() {
      return this.menuSections
        .map(section => ({
          ...section,
          links: section.links.filter(link => link.isVisible),
        }))
        .filter(section => section.links.length)
    },

    visibleFooterLinks() {
      return this.footerLinks.filter(link => link.isVisible)
    },

    safePermissions() {
      return this.permissions || []
    },

    safeIsOwnOrganizationScope() {
      if (!this.permissions) return false

      return isOwnOrganizationScope(this.permissions)
    },
  },
  methods: {
    featureAccess,
    checkPermissions,
    ...mapActions('user', ['logout']),

    buildMenuLink(route) {
      const { menu } = route.meta
      const title = this.resolveMenuTitle(menu, route)

      const link = {
        key: menu.key || route.name,
        section: menu.section,
        order: menu.order || 0,
        title,
        tooltip: title,
        to: this.resolveMenuTo(menu, route),
        isVisible: this.resolveMenuVisibility(menu),
        icon: menu.icon,
        activeIcon: menu.activeIcon,
      }

      PASSTHROUGH_FIELDS.forEach(field => {
        if (menu[field] !== undefined) link[field] = menu[field]
      })

      if (menu.counter === 'unreadNotifications') {
        link.counter = this.unreadNotificationCount
        link.counterLimit = 1000
        link.shortCounterLimit = 99
      }

      if (menu.create && this.resolveCreateVisibility(menu.create)) {
        link.create = {
          tooltip: menu.create.label,
          to: menu.create.route
            ? { name: menu.create.route }
            : this.withCreateQuery(link.to),
        }
      }

      return link
    },

    // The quick-create target: a dedicated route when one exists, otherwise the
    // page itself entered with ?state=create (picked up by createOnOpenMixin).
    withCreateQuery(to) {
      if (typeof to === 'string') return { path: to, query: { state: 'create' } }

      return { ...to, query: { ...to.query, state: 'create' } }
    },

    resolveCreateVisibility(create) {
      if (create.permissions) return this.checkPermissions(this.safePermissions, create.permissions)

      return true
    },

    resolveMenuTitle(menu, route) {
      // The one dynamic label: an org admin manages a single organization.
      if ((menu.key || route.name) === 'users') {
        return this.safeIsOwnOrganizationScope ? 'Organization' : 'Organizations'
      }

      return menu.title || route.meta.title
    },

    resolveMenuTo(menu, route) {
      if (!route.name) return route.path
      if (menu.query) return { name: route.name, query: menu.query }

      // A default child (path: '') resolves with a trailing slash; the menu
      // wants the clean path so active-route matching keeps working.
      const { route: resolved } = this.$router.resolve({ name: route.name })
      const path = resolved.path.length > 1 && resolved.path.endsWith('/')
        ? resolved.path.slice(0, -1)
        : resolved.path

      return path || { name: route.name }
    },

    // Declarative access, two optional vetoes in order: `feature` is a build
    // flag, `permissions` is a plain checkPermissions call.
    resolveMenuVisibility(menu) {
      if (menu.feature && !this.featureAccess(menu.feature)) return false
      if (menu.permissions) return this.checkPermissions(this.safePermissions, menu.permissions)

      return true
    },

    contactSupport() {
      window.location.href = 'mailto:support@glprotocol.com'
    },

    async logoutUser() {
      localStorage.removeItem('caseId')
      await this.logout();
      window.location.assign('/');
    },
  },
}
